// src/components/editable/AddEventButton.tsx
// Button + popover to create a new event

import React, { useMemo, useState } from "react";
import {
  Chip,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  Box,
  CircularProgress,
  Divider,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { FieldChoice } from "hooks/useFieldSchema";
import { useTranslation } from "../../i18n";
import logger from "utils/logger";

interface AddEventButtonProps {
  /** Event type choices from schema (order as defined on the Django model). */
  eventTypeChoices: FieldChoice[];
  /**
   * Optional groupings from `useFieldGroups().getEventTypeCategories()`.
   * If present, the popover renders sub-headers per group and sorts the
   * event types alphabetically within each group. Any event type not in
   * a group falls into a trailing "Other" bucket. If absent, the list is
   * sorted alphabetically by `display_name` (no subheaders).
   */
  eventTypeCategories?: Record<string, string[]>;
  /** Called when user selects an event type to create */
  onCreateEvent: (eventType: string) => Promise<void>;
  /** Loading state */
  loading?: boolean;
  /** Override the chip label (defaults to "Add Event") */
  label?: string;
  /** "onDark" for royal-blue / dark headers — uses white-based colors */
  variant?: "default" | "onDark";
}

const AddEventButton: React.FC<AddEventButtonProps> = ({
  eventTypeChoices,
  eventTypeCategories,
  onCreateEvent,
  loading = false,
  label,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const chipLabel = label || t("add_event.title", "Add Event");
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [creating, setCreating] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectEventType = async (eventType: string) => {
    if (creating) return;
    handleClose();
    setCreating(true);
    try {
      await onCreateEvent(eventType);
    } catch (err) {
      logger.error("Failed to create event:", err);
    } finally {
      setCreating(false);
    }
  };

  const open = Boolean(anchorEl);

  // Filter out null choices
  const validChoices = eventTypeChoices.filter(c => c.value !== null && c.value !== "");

  // Build the grouped, alphabetised render list. When `eventTypeCategories`
  // is provided, each event type appears in its assigned group (sorted
  // alphabetically inside) and anything not in any group falls into a
  // trailing "Other" bucket. Without groupings, fall back to a single
  // alphabetised list with no subheaders.
  const groupedChoices: { group: string | null; items: FieldChoice[] }[] = useMemo(() => {
    if (validChoices.length === 0) return [];
    const byValue = new Map<string, FieldChoice>();
    for (const c of validChoices) byValue.set(String(c.value), c);
    const byLabelAsc = (a: FieldChoice, b: FieldChoice) =>
      (a.display_name || "").localeCompare(b.display_name || "");

    if (!eventTypeCategories || Object.keys(eventTypeCategories).length === 0) {
      return [{ group: null, items: [...validChoices].sort(byLabelAsc) }];
    }
    const groups: { group: string; items: FieldChoice[] }[] = [];
    const placed = new Set<string>();
    for (const [groupName, slugs] of Object.entries(eventTypeCategories)) {
      const items = slugs
        .map((s) => {
          placed.add(s);
          return byValue.get(s);
        })
        .filter((c): c is FieldChoice => Boolean(c))
        .sort(byLabelAsc);
      if (items.length > 0) groups.push({ group: groupName, items });
    }
    const leftover = validChoices
      .filter((c) => !placed.has(String(c.value)))
      .sort(byLabelAsc);
    if (leftover.length > 0) groups.push({ group: "Other", items: leftover });
    return groups;
  }, [validChoices, eventTypeCategories]);

  if (validChoices.length === 0) {
    return null;
  }

  // Stop wheel events at the list edge so scrolling inside the dropdown
  // doesn't bleed through to the editable table behind it. (`onWheelCapture`
  // catches the event before it bubbles past the listbox.)
  const stopWheelLeak = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <Chip
        icon={
          loading || creating ? (
            <CircularProgress size={12} sx={{ color: "inherit" }} />
          ) : (
            <AddIcon sx={{ fontSize: 14 }} />
          )
        }
        label={chipLabel}
        size="small"
        onClick={loading || creating ? undefined : handleClick}
        disabled={loading || creating}
        sx={{
          height: 24,
          fontSize: "0.72rem",
          fontWeight: 500,
          cursor: loading || creating ? "default" : "pointer",
          backgroundColor: variant === "onDark"
            ? alpha(theme.palette.common.white, 0.12)
            : alpha(theme.palette.info.main, 0.1),
          color: variant === "onDark"
            ? alpha(theme.palette.common.white, 0.85)
            : theme.palette.info.main,
          border: variant === "onDark"
            ? `1px solid ${alpha(theme.palette.common.white, 0.2)}`
            : "1px solid transparent",
          backdropFilter: "blur(8px)",
          transition: "all 0.25s ease",
          "& .MuiChip-icon": {
            color: variant === "onDark"
              ? alpha(theme.palette.common.white, 0.85)
              : theme.palette.info.main,
          },
          "&:hover": {
            backgroundColor: variant === "onDark"
              ? alpha(theme.palette.common.white, 0.22)
              : alpha(theme.palette.primary.main, 0.18),
            color: variant === "onDark"
              ? theme.palette.common.white
              : theme.palette.primary.main,
            borderColor: variant === "onDark"
              ? alpha(theme.palette.common.white, 0.4)
              : alpha(theme.palette.primary.main, 0.4),
            "& .MuiChip-icon": {
              color: variant === "onDark"
                ? theme.palette.common.white
                : theme.palette.primary.main,
            },
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { width: 300, maxHeight: 480 },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Typography variant="subtitle2">
            {t("add_event.select_type", "Select event type to create")}
          </Typography>
        </Box>
        <Divider />
        <List
          dense
          onWheelCapture={stopWheelLeak}
          sx={{ maxHeight: 420, overflow: "auto", py: 0 }}
          subheader={<li />}
        >
          {groupedChoices.map(({ group, items }) => (
            <React.Fragment key={group ?? "__ungrouped__"}>
              {group != null && (
                <ListSubheader
                  disableSticky={false}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    color: theme.palette.text.secondary,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    lineHeight: "22px",
                    pl: 1.5,
                  }}
                >
                  {group}
                </ListSubheader>
              )}
              {items.map((choice) => (
                <ListItemButton
                  key={String(choice.value)}
                  onClick={() => handleSelectEventType(String(choice.value))}
                  disabled={creating}
                  sx={{ py: 0.25, minHeight: 28, pl: 2 }}
                >
                  <ListItemText
                    primary={choice.display_name}
                    primaryTypographyProps={{ variant: "body2", sx: { fontSize: "0.8rem" } }}
                  />
                </ListItemButton>
              ))}
            </React.Fragment>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default AddEventButton;
